import { clsx } from 'clsx'
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { Link, type LinkProps } from 'react-router'
import { Spinner } from './spinner'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'record'
type Size = 'sm' | 'md' | 'lg' | 'icon-sm' | 'icon' | 'icon-lg' | 'icon-round' | 'pill'

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-strong disabled:bg-accent/50',
  secondary: 'border border-border bg-surface-2 text-text hover:border-border-strong hover:bg-surface-3',
  ghost: 'text-muted hover:bg-surface-2 hover:text-text',
  danger: 'bg-danger-soft text-danger hover:bg-danger/25',
  record: 'bg-rec text-white hover:bg-rec/85',
}

const sizes: Record<Size, string> = {
  sm: 'h-8 gap-1.5 rounded-lg px-3 text-[13px]',
  md: 'h-10 gap-2 rounded-lg px-4 text-sm',
  lg: 'h-12 gap-2 rounded-lg px-6 text-[15px]',
  'icon-sm': 'size-8 justify-center rounded-lg',
  icon: 'size-9 justify-center rounded-lg',
  'icon-lg': 'size-12 justify-center rounded-lg',
  /** The editor's play button. */
  'icon-round': 'size-10 justify-center rounded-full',
  /** Matches the studio's round controls; icon only on phones. */
  pill: 'h-11 gap-2 rounded-full px-5 text-sm max-sm:w-11 max-sm:px-0',
}

export function buttonClass(variant: Variant = 'secondary', size: Size = 'md', className?: string) {
  return clsx(
    'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap transition-colors select-none disabled:cursor-not-allowed disabled:opacity-60',
    variants[variant],
    sizes[size],
    className,
  )
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  ref?: Ref<HTMLButtonElement>
}

export function Button({ variant = 'secondary', size = 'md', loading, icon, className, children, disabled, type = 'button', ref, ...rest }: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={buttonClass(variant, size, className)}
      {...rest}
    >
      {loading ? <Spinner className="size-4 text-current" /> : icon}
      {children}
    </button>
  )
}

/** A link that looks like a button; never nest a <Button> inside a <Link>. */
export function LinkButton({ variant, size, icon, className, children, ...rest }: LinkProps & { variant?: Variant; size?: Size; icon?: ReactNode }) {
  return (
    <Link className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      {children}
    </Link>
  )
}

/** Same for plain downloads and API links that must not go through the router. */
export function AnchorButton({
  variant,
  size,
  icon,
  className,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant; size?: Size; icon?: ReactNode }) {
  return (
    <a className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      {children}
    </a>
  )
}
